import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { irisBrief, irisCheck, irisNote, irisOutput, irisRejected } from '@engineer-os/engine';
import { api } from '../api';
import { track } from '../lib/events';
import { getLocale } from '../lib/locale';
import { readTerminal } from '../lib/terminal-tail';
import {
  askIris,
  awaitingIris,
  newIrisConversation,
  openIris,
  pendingStep,
  terminalRuntimeFailure,
  withRefusal,
  withStep,
  withUser,
  type IrisConversation,
} from '../lib/iris';
import type { PublicMission } from '../types';

/**
 * Iris's loop, for the level on screen (docs/iris.md, "The panel").
 *
 * The Worker answers one step per call and remembers nothing, so the loop is
 * this hook: ask for a step, wait for the learner, do what they approved,
 * record what came of it, wait for the learner to ask again. Nothing here acts on its own. A step
 * is only ever run from `run`, which only the panel's Run it button calls.
 *
 * ## The conversation belongs to the workspace
 *
 * For the reason the mentor's transcript is `useSession`'s: the play screen
 * is not remounted between levels, and every sentence in a conversation is
 * about one tree. So it is replaced outright — id, turns and log — when the
 * level changes, when the session does, and when `resetKey` moves, which the
 * session bumps on a reset. A step that lands after any of those is about a
 * workspace nobody is looking at, and is dropped.
 */

export type IrisBusy = 'thinking' | 'running' | 'checking' | null;

export interface IrisApi {
  conversation: IrisConversation;
  busy: IrisBusy;
  /** The approved command could not be run at all; the step is still on the table. */
  runFailed: boolean;
  /** The first turn: the brief, and the learner's request if they wrote one. */
  start(request: string): void;
  /** Run the proposed command. The panel's Run it button is the only caller. */
  run(): void;
  /** Press Check my work for the step that proposed it. */
  check(): void;
  reject(note: string): void;
  /** The learner's own words, instead of the step on the table or after `done`. */
  note(text: string): void;
  /** Ask about a recorded result, or retry a refusal that left a turn unanswered. */
  retry(): void;
  /** A fresh conversation, after the conversation's `capped`. */
  restart(): void;
  /** The signed-out door, or a lapsed sign-in. */
  signIn(): void;
}

export interface UseIrisOptions {
  mission: PublicMission;
  sessionId: string | null;
  /** Moves on every workspace reset (`useSession().reconnectKey`). */
  resetKey: number;
  /** After an approved command ran: the refresh a command at the prompt gets. */
  onRan(): void;
  /** Check my work, through the screen's own handler; resolves to what the learner saw. */
  onCheck(): Promise<string>;
  onSignIn(): void;
}

export function useIris({ mission, sessionId, resetKey, onRan, onCheck, onSignIn }: UseIrisOptions): IrisApi {
  // What Iris is told the task is: title, brief, visible objectives and
  // constraints, as the learner reads them (engine irisBrief, shared with the
  // Worker and scripts/iris/measure.mjs).
  const briefText = useMemo(() => irisBrief(mission), [mission]);
  const [conversation, setConversation] = useState<IrisConversation>(newIrisConversation);
  const [busy, setBusyState] = useState<IrisBusy>(null);
  // The same, synchronously. Two presses inside one render both see the
  // state as it was, and the second would send the conversation twice.
  const working = useRef<IrisBusy>(null);
  const setBusy = useCallback((next: IrisBusy) => {
    working.current = next;
    setBusyState(next);
  }, []);
  const [runFailed, setRunFailed] = useState(false);
  // The conversation as of the last change, for the async steps below: each
  // of them awaits something and then has to know whether the conversation
  // it started with is still the one on screen.
  const current = useRef(conversation);
  const crashSeen = useRef<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const commit = useCallback((next: IrisConversation) => {
    current.current = next;
    setConversation(next);
  }, []);

  useEffect(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    commit(newIrisConversation());
    setBusy(null);
    setRunFailed(false);
  }, [mission.id, sessionId, resetKey, commit, setBusy]);

  useEffect(() => () => inFlight.current?.abort(), []);

  const count = useCallback((detail: string) => {
    track('iris-step', { level: mission.level, track: mission.track, detail });
  }, [mission.level, mission.track]);

  const send = useCallback(async (posted: IrisConversation) => {
    // Send only a fixed classification, never terminal history or file contents.
    const runtimeFailure = terminalRuntimeFailure(sessionId ? readTerminal(sessionId) : undefined);
    if (runtimeFailure && crashSeen.current !== posted.id) {
      posted = withUser(posted, runtimeFailure);
      crashSeen.current = posted.id;
    }
    if (!runtimeFailure) crashSeen.current = null;
    commit(posted);
    setBusy('thinking');
    const controller = new AbortController();
    inFlight.current = controller;
    const answer = await askIris({
      missionId: mission.id,
      conversationId: posted.id,
      locale: getLocale(),
      brief: briefText,
      turns: posted.turns,
    }, controller.signal);
    if (controller.signal.aborted || current.current.id !== posted.id) return;
    inFlight.current = null;
    setBusy(null);
    if (answer.outcome === 'step') {
      commit(withStep(current.current, answer.step, answer.usage));
      count(answer.step.action.kind === 'done' ? 'done' : 'proposed');
    } else {
      commit(withRefusal(current.current, answer.outcome, answer.outcome === 'capped' ? answer.cap ?? null : null));
      count(answer.outcome);
    }
  }, [mission.id, sessionId, briefText, commit, count, setBusy]);

  const start = useCallback((request: string) => {
    if (working.current || current.current.turns.length > 0) return;
    void send(openIris(current.current, briefText, mission.cwd, request));
  }, [send, briefText, mission.cwd]);

  const run = useCallback(async () => {
    const step = pendingStep(current.current);
    if (working.current || !sessionId || step?.action.kind !== 'run') return;
    const { command } = step.action;
    const id = current.current.id;
    setBusy('running');
    setRunFailed(false);
    let result: { stdout: string; stderr: string; code: number };
    try {
      result = await api.irisRun(sessionId, command);
    } catch {
      if (current.current.id === id) {
        setBusy(null);
        setRunFailed(true);
      }
      return;
    }
    if (current.current.id !== id) return;
    onRan();
    count('ran');
    const text = irisOutput(command, result.code, result.stdout, result.stderr);
    commit(withUser(current.current, text, [{ kind: 'output', text }], 'ran'));
    setBusy(null);
  }, [sessionId, onRan, count, commit, setBusy]);

  const check = useCallback(async () => {
    if (working.current || pendingStep(current.current)?.action.kind !== 'check') return;
    const id = current.current.id;
    setBusy('checking');
    setRunFailed(false);
    let summary: string;
    try {
      summary = await onCheck();
    } catch {
      summary = 'Check my work gave no result.';
    }
    if (current.current.id !== id) return;
    count('check');
    commit(withUser(current.current, irisCheck(summary), [{ kind: 'check', text: summary }], 'checked'));
    setBusy(null);
  }, [onCheck, count, commit, setBusy]);

  const reject = useCallback((note: string) => {
    if (working.current || !pendingStep(current.current)) return;
    setRunFailed(false);
    count('rejected');
    const said = note.trim();
    void send(withUser(current.current, irisRejected(said), said ? [{ kind: 'said', text: said }] : [], 'rejected'));
  }, [count, send]);

  const note = useCallback((text: string) => {
    const said = text.trim();
    if (working.current || !said || current.current.turns.length === 0) return;
    setRunFailed(false);
    count('noted');
    void send(withUser(current.current, irisNote(said), [{ kind: 'said', text: said }], 'noted'));
  }, [count, send]);

  const retry = useCallback(() => {
    if (working.current || !awaitingIris(current.current)) return;
    void send({ ...current.current, refusal: null });
  }, [send]);

  const restart = useCallback(() => {
    if (working.current) return;
    setRunFailed(false);
    commit(newIrisConversation());
  }, [commit]);

  const signIn = useCallback(() => {
    // Counted before the window opens, as the mentor's door is: what is
    // measured is the door being reached.
    count('sign-in');
    onSignIn();
  }, [count, onSignIn]);

  return {
    conversation,
    busy,
    runFailed,
    start,
    run: () => void run(),
    check: () => void check(),
    reject,
    note,
    retry,
    restart,
    signIn,
  };
}
